<template>
  <div>
    <div v-if="!isReadOnly">
      <b-sidebar
        ref="layoutSidebar"
        v-model="visible"
        class="prevent-unselect layout-sidebar"
        bg-variant="white"
        header-class="border-bottom p-3 mb-2"
        shadow
        z-index="1040"
        :title="htmlTemplateName"
        :footer-class="['px-3 py-2']"
        :width="width"
        :right="right"
        @change="$emit('toggle')"
      >
        <div class="layout-sidebar__shell">
          <b-tabs
            v-if="selectedInstance"
            v-model="tabIndex"
            class="no-gutters h-100 layout-sidebar__tabs"
            nav-wrapper-class="layout-sidebar-nav-wrapper"
            :content-class="sidebarContentClass"
            small
            pills
            vertical
            end
            :nav-class="target ? '' : 'no-target'"
            @input="onSidebarTabInput"
          >
            <b-tab>
              <template #title>
                <TabTitle
                  title="Settings"
                  icon="sliders"
                />
              </template>
              <template v-if="target">
                <b-link
                  v-b-tooltip.hover.left.viewport.noninteractive="`Go to layer`"
                  class="layer-name-link d-inline-block"
                  @click="goToLayersTab"
                >
                  {{ layerName }}
                </b-link>
                <b-alert
                  v-if="isElementRestricted"
                  show
                  variant="info"
                  class="mb-2 py-2 px-3 small"
                >
                  <b-icon
                    icon="info-circle"
                    class="mr-1"
                  />
                  {{ elementRestrictions.reason }}
                </b-alert>
                <TextEditorControl
                  v-if="typeEditorControl === 'TEXT'"
                  id="text-editor-control-settings"
                  ref="textEditorControl"
                  :key="t"
                  :target="target"
                  :options="options"
                  :themes="themes"
                  :field-type="fieldType"
                  :is-open="isOpen"
                  @toggleSection="toggleSection"
                />
                <ShapeEditorControl
                  v-else-if="typeEditorControl === 'SHAPE'"
                  :target="target"
                  :themes="themes"
                  :is-open="isOpen"
                  @toggleSection="toggleSection"
                />
                <ImageEditorControl
                  v-else-if="typeEditorControl === 'IMAGE'"
                  :target="target"
                  :options="options"
                  :themes="themes"
                  :is-tab-active="tabIndex === TAB_INDEX.SETTINGS"
                  :is-open="isOpen"
                  @toggleSection="toggleSection"
                  @toggle="$emit('toggle')"
                />
                <ProductDimensionEditorControl
                  v-else-if="typeEditorControl === 'PRODUCT_DIMENSION'"
                  :options="options"
                  :is-open="isOpen"
                  :target="target"
                  @toggleSection="toggleSection"
                  @toggle="$emit('toggle')"
                />
                <GroupEditorControl
                  v-else-if="typeEditorControl === 'GROUP'"
                  :themes="themes"
                  :options="options"
                  :target="target"
                  :is-open="isOpen"
                  @toggleSection="toggleSection"
                  @unselected="switchToTab(TAB_INDEX.EFFECTS)"
                />
                <SmartObjectEditorControl
                  v-else-if="typeEditorControl === 'SMART_OBJECT'"
                  :options="options"
                  :preview="target.preview"
                  :smart-objects-list="target.smartObjectsList"
                  :target="target"
                  :smart-object-id="target?.smartObjectId"
                  :is-open="isOpen"
                  @toggleSection="toggleSection"
                  @focus-textarea="focusTextarea"
                />
              </template>
            </b-tab>
            <b-tab>
              <template #title>
                <TabTitle
                  title="Properties"
                  icon="gear-wide-connected"
                />
              </template>
              <PropertiesModule
                :target="target"
                :options="modules.properties"
              />
            </b-tab>
            <b-tab title-item-class="instance-related">
              <template #title>
                <TabTitle
                  title="Layers"
                  icon="layers"
                />
              </template>
              <LayersModule
                ref="layersModule"
                :is-tab-active="tabIndex === TAB_INDEX.LAYERS"
              />
            </b-tab>
            <b-tab title-item-class="instance-related">
              <template #title>
                <TabTitle
                  title="Content"
                  icon="images"
                />
              </template>
              <ContentModule
                :switch-to-tab="switchToTab"
              />
            </b-tab>
            <b-tab>
              <template #title>
                <TabTitle
                  title="Shadows"
                  icon="back"
                />
              </template>
              <ShadowsModule :target="target" />
            </b-tab>
            <b-tab title-item-class="instance-related">
              <template #title>
                <TabTitle
                  title="Translate"
                  icon="translate"
                />
              </template>
              <TranslateModule
                :is-open="isOpen"
                :target="backgroundColorRectangle"
                :options="options"
                @unselected="goToFieldsTab"
                @toggleSection="toggleSection"
              />
            </b-tab>
            <b-tab :title-item-class="{ 'd-none': typeEditorControl !== 'TEXT' }">
              <template #title>
                <TabTitle
                  title="Gradient Fill"
                  icon="paint-bucket"
                />
              </template>
              <TextColorEditor
                v-if="target"
                :height="target?.height || 100"
                :target="target"
                :width="target?.width || 200"
                @gradientApplied="applyTextGradient"
              />
            </b-tab>
            <b-tab :title-item-class="{ 'd-none': typeEditorControl !== 'TEXT' }">
              <template #title>
                <TabTitle
                  title="Effects"
                  icon="brush"
                />
              </template>
              <EffectsModule :target="target" />
            </b-tab>
            <b-tab>
              <template #title>
                <TabTitle
                  title="Animations"
                  icon="collection-play"
                />
              </template>
              <AnimationsModule
                :target="target"
                :options="modules.animation"
              />
            </b-tab>
            <b-tab title-item-class="instance-related">
              <template #title>
                <TabTitle
                  title="Fields"
                  icon="diagram3"
                />
              </template>
              <FieldsModule 
                :is-open="isOpen"
                :target="backgroundColorRectangle"
                :options="options"
                :type-editor-control="typeEditorControl"
                @unselected="goToFieldsTab" 
                @toggleSection="toggleSection"
              />
            </b-tab>
            <b-tab
              v-show="backgroundColorRectangle"
              title-item-class="instance-related"
            >
              <template #title>
                <TabTitle title="Background Color Editor">
                  <b-iconstack class="overflow-hidden">
                    <b-icon
                      stacked
                      icon="square"
                    />
                    <b-icon
                      stacked
                      scale="1.5"
                      rotate="-45"
                      icon="justify"
                    />
                  </b-iconstack>
                </TabTitle>
              </template>
              <BackgroundColorEditor 
                v-if="backgroundColorRectangle"
                :height="backgroundColorRectangle?.height || 0"
                :target="backgroundColorRectangle"
                :width="backgroundColorRectangle?.width || 0"
              />
            </b-tab>
            <b-tab title-item-class="instance-related">
              <template #title>
                <TabTitle
                  title="Theme"
                  icon="palette"
                />
              </template>
              <ThemeModule />
            </b-tab>
            <b-tab
              v-show="layoutId"
              title-item-class="instance-related"
            >
              <template #title>
                <TabTitle
                  title="Comments"
                  icon="chat-left-text"
                >
                  <span
                    v-if="unresolvedCommentsCount > 0"
                    class="badge badge-danger ml-1"
                  >
                    {{ unresolvedCommentsCount }}
                  </span>
                </TabTitle>
              </template>
              <CommentsPanel
                :layout-id="layoutId"
                :is-tab-active="tabIndex === TAB_INDEX.COMMENTS"
                @comments-updated="updateCommentsCount"
              />
            </b-tab>
            <b-tab
              v-show="layoutId"
              title-item-class="instance-related layout-sidebar-brief-tab"
            >
              <template #title>
                <TabTitle title="Brief">
                  <svg
                    class="sidebar-custom-icon"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <rect
                      width="8"
                      height="4"
                      x="8"
                      y="2"
                      rx="1"
                      ry="1"
                    />
                    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                    <path d="M12 11h4" />
                    <path d="M12 16h4" />
                    <path d="M8 11h.01" />
                    <path d="M8 16h.01" />
                  </svg>
                </TabTitle>
              </template>
              <BriefPanel
                :key="linkedBrief && linkedBrief.id ? linkedBrief.id : 'no-brief'"
                :layout-id="layoutId"
                :customer-id="customerId"
                :initial-linked-brief="linkedBrief"
                @updated="updateLinkedBrief"
              />
            </b-tab>
            <b-tab title-item-class="instance-related">
              <template #title>
                <TabTitle
                  title="Screen Recording"
                  icon="display"
                />
              </template>
              <VideoScreenRecording />
            </b-tab>
            <b-tab
              title-item-class="instance-related layout-sidebar-clipboard-tab"
              :title-link-class="clipboardNavLinkClass"
            >
              <template #title>
                <TabTitle title="Clipboard">
                  <svg class="sidebar-custom-icon">
                    <use
                      width="17.5"
                      height="17.5"
                      href="/img/icons/clipboard.svg#icon-clipboard"
                    />
                  </svg>
                </TabTitle>
              </template>
            </b-tab>
            <b-tab title-item-class="instance-related layout-sidebar-ai-tab">
              <template #title>
                <TabTitle title="Optix AI">
                  <svg class="sidebar-custom-icon">
                    <use
                      width="17.5"
                      height="17.5"
                      href="/img/icons/sparkles.svg#icon-sparkles"
                    />
                  </svg>
                </TabTitle>
              </template>
              <AIChatModule
                v-if="!target || (target && target.type !== 'SMART_OBJECT')"
                ref="aiChatModule"
                :is-tab-active="tabIndex === designAiTabIndex"
                :target-element="target"
              />
            </b-tab>
            <template #tabs-end>
              <b-button
                v-b-tooltip.hover="`${right ? 'Left' : 'Right'} side`"
                class="my-auto ml-auto mr-1 d-none"
                variant="primary"
                size="sm"
                @click="right = !right"
              >
                <b-icon :icon="`arrow-bar-${right ? 'left' : 'right'}`" />
              </b-button>
            </template>
          </b-tabs>
        </div>
        <template #footer>
          <div
            class="layout-sidebar-footer"
            :class="{ 'layout-sidebar-footer--ai-active': tabIndex === designAiTabIndex }"
          >
            <ul
              v-if="selectedInstance"
              class="layout-sidebar-footer-nav nav nav-pills flex-column"
              role="tablist"
            >
              <li
                class="nav-item layout-sidebar-clipboard-tab"
                role="presentation"
              >
                <a
                  href="#"
                  role="button"
                  class="nav-link"
                  :class="clipboardNavLinkClass"
                  @click.prevent="onFooterClipboardClick"
                >
                  <TabTitle title="Clipboard">
                    <svg class="sidebar-custom-icon">
                      <use
                        width="17.5"
                        height="17.5"
                        href="/img/icons/clipboard.svg#icon-clipboard"
                      />
                    </svg>
                  </TabTitle>
                </a>
              </li>
              <li
                class="nav-item layout-sidebar-ai-tab"
                role="presentation"
              >
                <a
                  href="#"
                  role="button"
                  class="nav-link"
                  :class="{ active: tabIndex === designAiTabIndex }"
                  @click.prevent="goToAiChatTab"
                >
                  <TabTitle title="Optix AI">
                    <svg class="sidebar-custom-icon">
                      <use
                        width="17.5"
                        height="17.5"
                        href="/img/icons/sparkles.svg#icon-sparkles"
                      />
                    </svg>
                  </TabTitle>
                </a>
              </li>
            </ul>
            <div
              v-if="tabIndex !== designAiTabIndex"
              class="layout-sidebar-footer-meta"
            >
              <span class="text-muted">{{ fieldType === "Multi Line Text" ? "Text" : fieldType }}</span>
            </div>
          </div>
        </template>
      </b-sidebar>
      <ClipboardDrawer
        v-if="visible"
        v-model="showClipboard"
        sidebar-overlay
        portal-to-body
        :sidebar-on-right="right"
        :overlay-width="width"
        :anchor-rect="clipboardAnchorRect"
        insert-only
        :draft-text="layoutClipboardDraftText"
        @select="onClipboardInsert"
      />
    </div>
  </div>
</template>

<script>
/* global previews */
import { mapGetters } from 'vuex'
import AnimationsModule from './EditorControl/AnimationsModule.vue'
import PropertiesModule from './EditorControl/PropertiesModule.vue'
import TextEditorControl from './EditorControl/TextEditorControl.vue'
import FieldsModule from './EditorControl/FieldsModule.vue'
import ShapeEditorControl from './EditorControl/ShapeEditorControl.vue'
import ImageEditorControl from './EditorControl/ImageEditorControl.vue'
import ShadowsModule from './EditorControl/ShadowsModule.vue'
import ProductDimensionEditorControl from "./EditorControl/ProductDimensionEditorControl.vue";
import GroupEditorControl from "@frontend/components/modules/layouts-page/EditorControl/GroupEditorControl.vue";
import TabTitle from '@frontend/components/common/atoms/TabTitle.vue'
import LayersModule from '@frontend/components/modules/layouts-page/EditorControl/LayersModule.vue'
import SmartObjectEditorControl
  from "@frontend/components/modules/layouts-page/EditorControl/SmartObjectEditorControl.vue";
  
import TextColorEditor from '@frontend/components/modules/layouts-page/EditorControl/TextColorEditor.vue'
import BackgroundColorEditor from "@frontend/components/modules/layouts-page/EditorControl/BackgroundColorEditor.vue";
import ContentModule from './EditorControl/ContentModule.vue';
import AIChatModule from './EditorControl/components/AIChat/AIChatModule.vue';
import ClipboardDrawer from '@frontend/components/common/clipboard/ClipboardDrawer.vue';
import VideoScreenRecording from "@frontend/components/modules/video-screen-recording/video-screen-recording.vue";
import {ShowPageScopeCollector} from "@frontend/group/modules/scope-collectors/show-page-collector";
import ThemeModule from "@frontend/components/modules/layouts-page/EditorControl/ThemeModule.vue";
import { drawTextWithGradient, enhanceTextWithGradient } from "@/common/preview/draw-multi-line-text";
import EffectsModule from "@frontend/components/modules/layouts-page/EditorControl/EffectsModule.vue";
import { TAB_INDEX } from '@frontend/constants/layout-sidebar-tabs';
import TranslateModule from "./EditorControl/TranslateModule";
import CommentsPanel from "./EditorControl/CommentsPanel.vue";
import BriefPanel from "./EditorControl/BriefPanel.vue";
import { editModeService } from "@frontend/services/EditModeService";

export default {
  name: 'LayoutSidebar',
  components: {
    TranslateModule,
    EffectsModule,
    ThemeModule,
    BackgroundColorEditor,
    LayersModule,
    TabTitle,
    SmartObjectEditorControl,
    GroupEditorControl,
    ShadowsModule,
    ImageEditorControl,
    ShapeEditorControl,
    FieldsModule,
    TextEditorControl,
    TextColorEditor,
    PropertiesModule,
    AnimationsModule,
    ProductDimensionEditorControl,
    ContentModule,
    AIChatModule,
    ClipboardDrawer,
    CommentsPanel,
    BriefPanel,
    VideoScreenRecording
  },

  provide() {
    return {
      layoutClipboardActions: {
        openFullDrawer: () => this.openFullClipboardDrawer(),
      },
    };
  },

  props: {
    isReadOnlyLayout: {
      type: Boolean,
      default: false,
    },
    initialLinkedBrief: {
      type: [Object, String],
      default: null,
    },
  },
  data: () => ({
    t: 0,
    typeEditorControl: null,
    visible: false,
    defaultTabIndex: TAB_INDEX.FIELDS,
    tabIndex: TAB_INDEX.FIELDS,
    width: '430px',
    right: true,
    target: null,
    themes: null,
    options: {},
    fieldType: '',
    modules: {
      animation: {},
      properties: {},
    },
    TAB_INDEX,
    layoutId: null,
    customerId: null,
    unresolvedCommentsCount: 0,
    linkedBrief: null,
    showClipboard: false,
    clipboardAnchorRect: null,
    suppressClipboardTabInput: false,
    previousTabIndex: TAB_INDEX.FIELDS,
    clipboardDraftRefreshKey: 0,
    isOpen: {
        actionSection: true,
        fillSection: true,
        borderSettingsSection: true,
        textEditorSection: true,
        textSettingsSection: true,
        relatedFieldSection: true,
        opacitySettingsSection: true,
        alignmentAndOpacitySection: true,
        alignmentButtonsSection: true,
        strokeAndBackgroundSection: true,
        smartObjectAndBackgroundSection: true,
        generalSettingsSection: true,
        membersSection: true,
        themeSection: true,
        borderSection: true,
        opacityAndScaleSection: true,
        productSection: true,
        productSettingsSection: true,
        imageSettingsSection: true,
        outlineSettingsSection: true,
        transformSection: true,
        objectSettingsSection: true,
        textEditorFieldsSection: true,
        translateFieldsSelection: true,
        backgroundFieldsSection: true,
        productImagesFieldsSection: true,
        imagesFieldSection: true
    },
  }),
  
  computed: {
    sidebarContentClass() {
      return this.tabIndex === TAB_INDEX.BRIEF
        ? 'h-100 layout-sidebar-content--brief'
        : 'px-3 py-2 h-100';
    },

    clipboardNavLinkClass() {
      return this.showClipboard ? 'active' : '';
    },

    clipboardTabIndex() {
      return TAB_INDEX.CLIPBOARD;
    },

    designAiTabIndex() {
      return TAB_INDEX.DESIGN_AI;
    },

    layoutClipboardDraftText() {
      void this.clipboardDraftRefreshKey;
      return this.resolveLayoutClipboardDraftText();
    },

    ...mapGetters('layout', [
      'selectedInstance',
    ]),
    layerName () {
      return this.target?.templateField?.name || this.target?.name
    },
    backgroundColorRectangle () {
      if (!this.selectedInstance || !this.selectedInstance.canvas || !this.selectedInstance.canvas.getObjects().length) {
        return null;
      }
      return this.selectedInstance.canvas.getObjects().find(object => object?.fieldType === 'Background Color')
    },
    isReadOnly() {
      return this.isReadOnlyLayout;
    },
    htmlTemplateName() {
      if (this.selectedInstance && this.selectedInstance.id) {
        const templateName = document.querySelector(`.template-item[data-instance="${this.selectedInstance.id}"] > div > div > h2`)?.textContent;
        return templateName || 'No selected item';

      } else {
        return 'No selected item';
      }

    },
    elementRestrictions() {
      if (!this.target) return null;
      return editModeService.getElementRestrictions(this.target);
    },
    isElementRestricted() {
      return this.elementRestrictions && !this.elementRestrictions.canEdit;
    },
    editModeLabel() {
      return editModeService.getEditModeLabel();
    },
    isEditModeRestricted() {
      return editModeService.isRestricted();
    },
  },
  watch: {
    visible(open) {
      if (!open) {
        this.showClipboard = false;
        this.clipboardAnchorRect = null;
        this.unbindClipboardBoundsListeners();
      }
      this.$nextTick(() => this.syncClipboardBackdrop());
    },
    showClipboard(open) {
      if (open) {
        this.refreshClipboardDraftText();
        this.syncClipboardAnchorRect();
        this.$nextTick(() => {
          this.refreshClipboardDraftText();
          this.syncClipboardAnchorRect();
          requestAnimationFrame(() => this.syncClipboardAnchorRect());
          setTimeout(() => this.syncClipboardAnchorRect(), 350);
        });
        this.bindClipboardBoundsListeners();
        this.bindClipboardDraftListeners();
      } else {
        this.clipboardAnchorRect = null;
        this.unbindClipboardBoundsListeners();
        this.unbindClipboardDraftListeners();
      }
      this.$nextTick(() => this.syncClipboardBackdrop());
    },
    tabIndex(newVal) {
      if (newVal === this.TAB_INDEX.TRANSLATE) {
        this.isOpen.translateFieldsSelection = true;
        localStorage.setItem('sectionStates', JSON.stringify(this.isOpen));
      }

      // Notify canvas about Shadows tab state
      if (this.selectedInstance?.canvas) {
        const isShadowsTabActive = newVal === this.TAB_INDEX.SHADOWS;
        this.selectedInstance.canvas.fire('shadowsTab:changed', {
          isActive: isShadowsTabActive
        });
      }
    },
    target () {
      this.ensureValidTabForTarget();
    },
  },

  created() {
    const defaults = { ...this.isOpen };
    const saved = localStorage.getItem('sectionStates');

    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        this.isOpen = { ...defaults, ...parsed };
      } catch (e) {
        this.isOpen = defaults;
      }
    } else {
      this.isOpen = defaults;
    }

    localStorage.setItem('sectionStates', JSON.stringify(this.isOpen));
    const grid = $("#template-group-preview");
    if (grid.length) {
      this.layoutId = grid.attr("data-layout-id");
      this.customerId = grid.attr("data-customer-id");
    }
    this.linkedBrief = this.normalizeLinkedBrief(this.initialLinkedBrief)
      || this.normalizeLinkedBrief(window.layoutInitialLinkedBrief);
    window.layoutInitialLinkedBrief = this.linkedBrief;

    if (localStorage.getItem("newSettingsFromAiBannerResizer")) {
      const aiBannerResizerSettings = JSON.parse(localStorage.getItem("newSettingsFromAiBannerResizer"));
      const customer_id = grid.attr("data-customer-id");
      const layout_id = grid.attr("data-layout-id");
      if (aiBannerResizerSettings.layoutId === layout_id && aiBannerResizerSettings.customerId === customer_id) {
        this.goToAiChatTab();
      }
    }

  },
  mounted() {
    window.addEventListener('ai-chat:open-tab', () => {
      this.goToAiChatTab();
    });

    $(document).on('click', '.template-name-canvas', function () {
      this.goToFieldsTab();
    }.bind(this));
  },
  beforeDestroy() {
    this.removeClipboardBackdrop();
    this.unbindClipboardBoundsListeners();
    this.unbindClipboardDraftListeners();
  },
  methods: {
    init(data) {
      if (!window.isFieldsObject) {
        if (data.forceShadowsTab) {
          this.visible = true
          this.tabIndex = TAB_INDEX.SHADOWS
        } else if (!this.visible || this.visible && this.tabIndex === this.defaultTabIndex) {
          this.visible = true
          this.tabIndex = TAB_INDEX.SETTINGS
        }
      }

      if (data && _.isEqual(data.target, this.target)) {
        // Don't return early - we want to update the sidebar even if target is the same
      }

      this.typeEditorControl = data.typeEditorControl
      this.fieldType = data.fieldType
      this.target = data.target
      this.$store.commit('layout/SET_TARGET', data.target)
      this.options = data.options
      this.modules = data.modules
      this.themes = data.themes
      this.isLayoutInitialized = true;
      window.refreshLayoutSidebar = this.refreshLayoutSidebar;
    },
    refreshLayoutSidebar(data) {
      console.log('refreshLayoutSidebar...');
      this.t = Date.now();
    },
    async createSmartObject() {
      const {rerenderTemplates, getUserLayersOrder} = ShowPageScopeCollector.get([
        'rerenderTemplates',
        'getUserLayersOrder',
      ]);
      const instance_id = this.target.preview.id;
      const template_id = this.target.preview.template.id;
      const template_width = this.target.preview.template?.width;
      const template_height = this.target.preview.template?.height;
      const name = this.newSmartObjectName;
      let users_fields_order = this.target.preview.data.users_fields_order ? JSON.parse(this.target.preview.data.users_fields_order) : false;
      let usersOrderData = null;
      if (!users_fields_order) {
        usersOrderData = getUserLayersOrder(instance_id);
      }
      this.selectedInstance.canvas.fire('add:smart-object-field', {
        instanceId: this.selectedInstance.id,
        type: 'Smart Object',
        options: {
          name: name,
          template_width: template_width,
          template_height: template_height,
          users_fields_order: usersOrderData ? usersOrderData.orders : users_fields_order,
        }
      })
      // const response = await axios.post(
      //   window.location.origin + '/banner/add-smart-object-field',
      //   {
      //     template_id,
      //     template_width,
      //     template_height,
      //     name,
      //     users_fields_order: usersOrderData ? usersOrderData.orders : users_fields_order,
      //     layout_id: $("#template-group-preview").attr("data-layout-id")
      //   }
      // );
      // await rerenderTemplates();
      //
      
    },
    goToLayersTab () {
      this.tabIndex = TAB_INDEX.LAYERS
    },
    goToFieldsTab () {
      this.tabIndex = TAB_INDEX.FIELDS
    },
    goToAiChatTab () {
      this.tabIndex = this.designAiTabIndex;
    },
    onFooterClipboardClick() {
      this.openFullClipboardDrawer();
    },
    openFullClipboardDrawer() {
      if (!this.showClipboard) {
        this.toggleClipboard();
      }
    },
    onSidebarTabInput(idx) {
      if (this.suppressClipboardTabInput) {
        this.suppressClipboardTabInput = false;
        return;
      }

      if (idx === this.clipboardTabIndex) {
        this.toggleClipboard();
        this.suppressClipboardTabInput = true;
        this.$nextTick(() => {
          this.tabIndex = this.previousTabIndex ?? this.defaultTabIndex;
        });
        return;
      }

      this.previousTabIndex = idx;

      if (this.showClipboard) {
        this.showClipboard = false;
      }
    },
    toggleClipboard() {
      const opening = !this.showClipboard;
      if (opening) {
        this.refreshClipboardDraftText();
        this.syncClipboardAnchorRect();
        this.showClipboard = true;
        this.$nextTick(() => {
          this.refreshClipboardDraftText();
          this.syncClipboardAnchorRect();
          requestAnimationFrame(() => this.syncClipboardAnchorRect());
        });
        return;
      }

      this.showClipboard = false;
      this.clipboardAnchorRect = null;
    },
    getLayoutSidebarElement() {
      return (
        this.$el?.querySelector('.b-sidebar-outer')
        || this.$refs.layoutSidebar?.$el
        || this.$el?.querySelector('.b-sidebar')
        || null
      );
    },
    refreshClipboardDraftText() {
      this.clipboardDraftRefreshKey += 1;
    },
    resolveLayoutClipboardDraftText() {
      const textEditor = this.getTextEditorFieldText();
      if (textEditor) {
        return textEditor;
      }

      const aiChat = this.getAiChatFieldText();
      if (aiChat) {
        return aiChat;
      }

      const focused = this.getFocusedSidebarFieldText();
      if (focused) {
        return focused;
      }

      return '';
    },
    getFocusedSidebarFieldText() {
      const root = this.getLayoutSidebarElement() || this.$el;
      const active = document.activeElement;

      if (!root || !active || !root.contains(active)) {
        return '';
      }

      if (active.matches('textarea, input')) {
        return String(active.value || '').trim() ? active.value : '';
      }

      if (active.isContentEditable) {
        const text = String(active.innerText || '').replace(/\n\n+/g, '\n').trim();
        return text;
      }

      return '';
    },
    getTextEditorFieldText() {
      const control = this.$refs.textEditorControl;
      if (!control) {
        return this.getSelectedTextLayerContent();
      }

      const input = control.$refs?.textEdit;
      const field = input?.$el?.querySelector?.('textarea, input');
      if (field) {
        const value = String(field.value || '').trim();
        if (value) {
          return field.value;
        }
      }

      const editorText = String(control.text || '').trim();
      if (editorText) {
        return control.text;
      }

      return this.getSelectedTextLayerContent();
    },
    getSelectedTextLayerContent() {
      if (this.typeEditorControl !== 'TEXT' || !this.target) {
        return '';
      }

      const raw = this.target.text ?? this.target.textLines?.map((line) => line.text).join('\n');
      return String(raw || '').trim() ? String(raw) : '';
    },
    getAiChatFieldText() {
      const chatTextArea = this.$refs.aiChatModule?.$refs?.editor?.$refs?.chatTextArea;
      if (!chatTextArea) {
        return '';
      }

      if (typeof chatTextArea.getText === 'function') {
        const text = String(chatTextArea.getText() || '').trim();
        if (text) {
          return chatTextArea.getText();
        }
      }

      const prompt = String(chatTextArea.prompt || '').trim();
      return prompt ? chatTextArea.prompt : '';
    },
    bindClipboardDraftListeners() {
      if (this._clipboardDraftInputBound) {
        return;
      }

      const root = this.getLayoutSidebarElement() || this.$el;
      if (!root) {
        return;
      }

      this._clipboardDraftInputHandler = () => this.refreshClipboardDraftText();
      root.addEventListener('input', this._clipboardDraftInputHandler, true);
      this._clipboardDraftInputBound = true;
    },
    unbindClipboardDraftListeners() {
      if (!this._clipboardDraftInputBound) {
        return;
      }

      const root = this.getLayoutSidebarElement() || this.$el;
      if (root && this._clipboardDraftInputHandler) {
        root.removeEventListener('input', this._clipboardDraftInputHandler, true);
      }

      this._clipboardDraftInputHandler = null;
      this._clipboardDraftInputBound = false;
    },
    syncClipboardAnchorRect() {
      const sidebar = this.getLayoutSidebarElement();
      if (!sidebar || !this.visible) {
        this.clipboardAnchorRect = null;
        return;
      }

      const rect = sidebar.getBoundingClientRect();
      if (rect.width < 1 || rect.height < 1) {
        return;
      }

      const box = {
        top: `${rect.top}px`,
        width: `${rect.width}px`,
        height: `${rect.height}px`,
      };

      if (this.right) {
        box.right = `${Math.max(0, window.innerWidth - rect.right)}px`;
        box.left = 'auto';
      } else {
        box.left = `${rect.left}px`;
        box.right = 'auto';
      }

      this.clipboardAnchorRect = box;
    },
    bindClipboardBoundsListeners() {
      if (this._clipboardBoundsBound) {
        return;
      }
      this._clipboardBoundsBound = true;
      window.addEventListener('resize', this.syncClipboardAnchorRect);
      window.addEventListener('scroll', this.syncClipboardAnchorRect, true);
    },
    unbindClipboardBoundsListeners() {
      if (!this._clipboardBoundsBound) {
        return;
      }
      this._clipboardBoundsBound = false;
      window.removeEventListener('resize', this.syncClipboardAnchorRect);
      window.removeEventListener('scroll', this.syncClipboardAnchorRect, true);
    },
    syncClipboardBackdrop() {
      const id = 'layout-sidebar-clipboard-backdrop';
      let el = document.getElementById(id);

      if (this.showClipboard && this.visible) {
        if (!el) {
          el = document.createElement('div');
          el.id = id;
          el.className = 'layout-sidebar-clipboard-backdrop';
          el.setAttribute('aria-hidden', 'true');
          Object.assign(el.style, {
            position: 'fixed',
            top: '0',
            right: '0',
            bottom: '0',
            left: '0',
            zIndex: '10040',
            background: 'rgba(15, 23, 42, 0.45)',
            pointerEvents: 'auto',
          });
          el.addEventListener('click', this.onClipboardBackdropClick);
          document.body.appendChild(el);
        }
        return;
      }

      this.removeClipboardBackdrop();
    },
    onClipboardBackdropClick() {
      this.showClipboard = false;
    },
    removeClipboardBackdrop() {
      const el = document.getElementById('layout-sidebar-clipboard-backdrop');
      if (el) {
        el.removeEventListener('click', this.onClipboardBackdropClick);
        el.remove();
      }
    },
    onClipboardInsert(item) {
      this.showClipboard = false;
      this.removeClipboardBackdrop();

      const editor = this.$refs.aiChatModule?.$refs?.editor;
      if (editor && typeof editor.setSuggestion === 'function') {
        editor.setSuggestion(item?.content || '');
        this.goToAiChatTab();
        return;
      }

      toastr.warning('Open Optix AI to insert into chat.');
    },
    rerenderLayersModule() {
      this.$nextTick(() => {
        if (!this.$refs.layersModule) return;
        this.$refs.layersModule.rerenderList();
      })
    },
    switchToTab(number = this.defaultTabIndex) {
      this.tabIndex = number;
    },
    toggleSection(section) {
      const next = !this.isOpen[section];

      if (Object.prototype.hasOwnProperty.call(this.isOpen, section)) {
        this.isOpen[section] = next;
      } else {
        this.$set(this.isOpen, section, next);
      }

      localStorage.setItem('sectionStates', JSON.stringify(this.isOpen));
    },
    async applyTextGradient(gradientSettings) {
      if (this.target && this.typeEditorControl === 'TEXT' && this.tabIndex === TAB_INDEX.GRADIENT_FILL) {
        // Store gradient settings on the text object for persistence
        this.target.gradientSettings = gradientSettings

        const finalTextObject = await enhanceTextWithGradient(this.target, gradientSettings)
        
        // Update object settings if available
        if (this.target.updateSettings) {
          this.target.updateSettings({
            ...this.target.getSettings?.() || {},
            gradientSettings: gradientSettings
          })
        }

        // Trigger canvas re-render
        if (this.target.canvas) {
          this.target.canvas.requestRenderAll()
          this.target.canvas.fire('gradient:changed', finalTextObject);
        }
      }
    },
    focusTextarea() {
      this.$nextTick(() => {
        this.$refs.textEditorControl.focusTextarea();
      });
    },
    updateCommentsCount(count) {
      this.unresolvedCommentsCount = count;
    },
    normalizeLinkedBrief(value) {
      if (value == null || value === '' || value === 'null') {
        return null;
      }

      let brief = value;
      if (typeof brief === 'string' || brief instanceof String) {
        try {
          brief = JSON.parse(String(brief));
        } catch (error) {
          console.error('Failed to parse linked brief:', error);
          return null;
        }
      }

      if (!brief || typeof brief !== 'object' || Array.isArray(brief) || !brief.id) {
        return null;
      }

      return brief;
    },
    updateLinkedBrief(brief) {
      this.linkedBrief = this.normalizeLinkedBrief(brief);
      window.layoutInitialLinkedBrief = this.linkedBrief;
    },
    isTabAvailableForTarget(tabIndex) {
      switch (tabIndex) {
        case this.TAB_INDEX.EFFECTS:
        case this.TAB_INDEX.GRADIENT_FILL:
          return this.typeEditorControl === 'TEXT';

        default:
          return true;
      }
    },
    ensureValidTabForTarget() {
      if (!this.isTabAvailableForTarget(this.tabIndex, this.target)) {
        this.tabIndex = this.TAB_INDEX.SETTINGS;
      }
    }
  },
}
</script>

<style scoped lang="scss">
::v-deep {
  .tab-pane {
    height: 100% !important;
    overflow-y: auto;
  }

  .b-sidebar {
    height: 100% !important;
  }

  .b-sidebar-body {
    flex: 1 1 0 !important;
  }

  .b-sidebar * {
    font-size: .875rem;
    line-height: 1.25rem;
  }

  // Brief panel owns type scale (!important in BriefPanel.vue)
  .b-sidebar .brief-panel {
    font-size: 14px;
    line-height: 1.5;
  }

  .b-sidebar-header {
    font-size: 1rem;
    overflow-wrap: anywhere;

    strong {
      font-size: 1rem;
      white-space: nowrap;
      width: 100%;
      overflow: hidden;
      -o-text-overflow: ellipsis;
      text-overflow: ellipsis;
    }

    .close {
      order: 2;
      margin-right: 5px !important;

      svg {
        font-size: 1.5rem;
      }
    }
  }

  .no-target {
    .nav-item:not(.instance-related) {
      display: none;
    }
  }

  .layout-sidebar-nav-wrapper {
    position: sticky;
    top: 0;
    align-self: flex-start;
    padding-right: .75rem;
    padding-top: .5rem;

    .nav-item.layout-sidebar-clipboard-tab,
    .nav-item.layout-sidebar-ai-tab {
      display: none !important;
    }

    .nav-link {
      padding: .75rem
    }
  }

  .layer-name-link {
    display: block;
    margin: -0.5rem 0 0.5rem 0;
  }

  .layout-sidebar-section {
    margin-bottom: 1rem;

    .collapse {
      padding: 0 0.75rem;
    }
  }

  .layout-sidebar-section-title {
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    user-select: none;
    padding: 0.75rem;
    margin-bottom: 0.75rem;
    border-radius: 0.5rem;
    background-color: hsla(210, 40%, 96.1%, 0.5);

    &:hover {
      background-color: hsl(210, 40%, 96.1%);
    }
  }
}

.sidebar-custom-icon {
  width: 17.5px;
  height: 17.5px;
}

::v-deep .layout-sidebar-content--brief {
  padding-left: 1.25rem !important;
  padding-right: 1.25rem !important;
  padding-top: 1rem !important;
  padding-bottom: 1rem !important;
}

.layout-sidebar-footer {
  position: relative;
  min-height: 5.5rem;
  width: 100%;
  padding: 0.5rem 0.75rem;
  overflow: visible;
}

.layout-sidebar-footer--ai-active {
  position: static;
  min-height: 0;
  height: 0 !important;
  padding: 0 !important;
  overflow: hidden;
}

.layout-sidebar-footer-nav {
  position: absolute;
  right: 0.75rem;
  bottom: 0.5rem;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: 0;
  padding: 0;
  list-style: none;

  .nav-link {
    padding: 0.5rem 0.75rem;
  }
}

.layout-sidebar-footer-meta {
  position: absolute;
  left: 0.75rem;
  right: 5rem;
  bottom: 0.75rem;
  text-align: right;
}

::v-deep .b-sidebar-body {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.layout-sidebar__shell {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.layout-sidebar__tabs {
  flex: 1 1 auto;
  min-height: 0;
}

::v-deep .b-sidebar-footer {
  flex-shrink: 0;
  padding: 0 !important;
  overflow: visible;
}

.layout-sidebar-clipboard-tab .tab-pane {
  display: none !important;
}

</style>
